import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { Purpose } from "config/constants"
import ReservationInvoicePosition from "models/ReservationInvoicePosition"
import { IReservationPricingSummaryItem } from "models/Reservation"
import ReservationCartInvoicePosition from "models/ReservationCartInvoicePosition"
import { InvoicePositionType } from "models/TenantInvoicePosition"

interface IUseTotalPricesParams {
    reservationInvoicePositions: (ReservationInvoicePosition | ReservationCartInvoicePosition)[]
    purpose?: Purpose
}

export default function useTotalPrices({
    reservationInvoicePositions,
    purpose,
}: IUseTotalPricesParams) {
    const { t } = useTranslation()

    const calculateTotalPrice = useCallback((type?: InvoicePositionType) => {
        let result = 0
        for (const ip of reservationInvoicePositions) {
            if (type && ip.type !== type) continue
            result += ip.amount || 0
        }
        return result
    }, [reservationInvoicePositions])

    const totalSalePrice = useMemo<number | null>(() => {
        if (purpose !== Purpose.sale || reservationInvoicePositions.length === 0) return null
        return calculateTotalPrice()
    }, [calculateTotalPrice, purpose, reservationInvoicePositions])

    const totalRecurringPrice = useMemo(() => {
        if (purpose === Purpose.sale || purpose === Purpose.relocation) return 0
        return calculateTotalPrice(InvoicePositionType.recurring)
    }, [calculateTotalPrice, purpose])

    const totalSinglePrice = useMemo(() => {
        if (purpose === Purpose.sale || purpose === Purpose.relocation) return 0
        return calculateTotalPrice(InvoicePositionType.single)
    }, [calculateTotalPrice, purpose])

    const totalPrices = useMemo<IReservationPricingSummaryItem[]>(() => {
        if (reservationInvoicePositions.length === 0) return []

        let items: IReservationPricingSummaryItem[] = []

        if (purpose === Purpose.sale) {
            items = [
                {
                    label: t('reservation.totalPrice'),
                    value: totalSalePrice || 0,
                }
            ]
        } else if (purpose !== Purpose.relocation) {
            items = [
                {
                    label: t('reservation.totalRecurringPrice'),
                    value: totalRecurringPrice,
                },
                {
                    label: t('reservation.totalSinglePrice'),
                    value: totalSinglePrice,
                }
            ]
        }

        return items
    }, [purpose, reservationInvoicePositions.length, t, totalRecurringPrice, totalSalePrice, totalSinglePrice])

    const isAmountOrTypeChanged = useMemo(() => {
        return reservationInvoicePositions.some(ip => "isAmountOrTypeChanged" in ip && ip.isAmountOrTypeChanged)
    }, [reservationInvoicePositions])

    return {
        totalSalePrice,
        totalPrices,
        isAmountOrTypeChanged,
    }
}
